import { createElement, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, ZoomIn, ZoomOut, Crosshair, Pencil, Check, Plus, Unlink, ChevronDown, ChevronRight, Store, Network } from "lucide-react";
import Avatar from "@/components/Avatar";
import { SERVICE_STYLE, avatarOf } from "./IntranetDirectory";

const inp = "px-2 py-1.5 rounded-lg border border-slate-200 text-xs outline-none focus:border-[#1d4ed8] bg-white";

export default function OrgChartModal({ open, onClose, onOpenPerson }) {
  const [data, setData] = useState(null);
  const [zoom, setZoom] = useState(0.85);
  const [edit, setEdit] = useState(false);
  const [region, setRegion] = useState("");
  const [service, setService] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [link, setLink] = useState({ child: "", parent: "" });
  const [dragId, setDragId] = useState(null);

  const load = () => api.get("/intranet-orgchart").then(r => { setData(r.data); }).catch(() => toast.error("Organigramme indisponible"));
  useEffect(() => { if (open) { load(); setZoom(0.85); } }, [open]);
  useEffect(() => { if (data) setTimeout(center, 100); }, [data]);

  const nodes = useMemo(() => Object.fromEntries((data?.nodes || []).map(n => [n.id, n])), [data]);
  const children = useMemo(() => {
    const m = {};
    Object.entries(data?.parents || {}).forEach(([c, p]) => { if (p && nodes[c]) (m[p] = m[p] || []).push(c); });
    Object.values(m).forEach(arr => arr.sort((a, b) => (nodes[a].kind === "corner") - (nodes[b].kind === "corner") || nodes[a].name.localeCompare(nodes[b].name, "fr")));
    return m;
  }, [data, nodes]);
  const orphans = useMemo(() => Object.keys(nodes).filter(id => !data?.parents?.[id] && id !== data?.root), [nodes, data]);

  const regions = useMemo(() => [...new Set(Object.values(nodes).map(n => n.region).filter(r => r && !r.startsWith("Siège")).flatMap(r => r.split(",").map(x => x.trim())))].sort(), [nodes]);
  const services = useMemo(() => [...new Set(Object.values(nodes).map(n => n.service).filter(Boolean))].sort(), [nodes]);
  const match = (n) => (!region || (n.region || "").includes(region)) && (!service || n.service === service);
  const visible = useMemo(() => {
    if (!region && !service) return null;
    const keep = new Set();
    const walk = (id) => { const kids = children[id] || []; let any = match(nodes[id]); kids.forEach(k => { if (walk(k)) any = true; }); if (any) keep.add(id); return any; };
    if (data?.root) walk(data.root);
    return keep;
  }, [region, service, children, nodes, data]);

  const setParent = async (childId, parentId) => {
    if (!childId || childId === parentId) return;
    let c = parentId; while (c) { if (c === childId) return toast.error("Lien circulaire impossible"); c = data.parents[c]; }
    try {
      await api.put("/intranet-orgchart/link", { child_id: childId, parent_id: parentId || null });
      toast.success(parentId ? "Lien mis à jour" : "Lien supprimé");
      if (parentId) { const open = {}; let c = parentId; while (c) { open[c] = false; c = data.parents[c]; } setCollapsed(cur => ({ ...cur, ...open })); }
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const center = () => { const el = document.getElementById("orgchart-scroll"); if (el) { el.scrollTo({ left: (el.scrollWidth - el.clientWidth) / 2, top: 0, behavior: "smooth" }); } };

  if (!open) return null;
  const people = Object.values(nodes).filter(n => n.kind !== "corner").sort((a, b) => a.name.localeCompare(b.name, "fr"));

  const ctx = { nodes, children, visible, edit, root: data?.root, collapsed, setCollapsed, dragId, setDragId, setParent, onOpenPerson };

  return (
    <div className="fixed inset-0 z-[60] bg-[#F4F6F9] flex flex-col" data-testid="orgchart-modal">
      <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
        <h2 className="flex items-center gap-2 text-base md:text-lg font-bold text-[#0b1b3a] mr-auto"><Network className="w-5 h-5 text-[#1d4ed8]"/>Organigramme du Groupe Orbit</h2>
        <select value={region} onChange={e => setRegion(e.target.value)} className={inp} data-testid="org-filter-region"><option value="">Toutes les régions</option>{regions.map(r => <option key={r}>{r}</option>)}</select>
        <select value={service} onChange={e => setService(e.target.value)} className={inp} data-testid="org-filter-service"><option value="">Tous les services</option>{services.map(s => <option key={s}>{s}</option>)}</select>
        <div className="inline-flex border border-slate-200 rounded-lg overflow-hidden bg-white">
          <button onClick={() => setZoom(z => Math.max(0.4, z - 0.1))} data-testid="org-zoom-out" className="p-1.5 hover:bg-slate-50"><ZoomOut className="w-4 h-4"/></button>
          <span className="px-2 text-xs self-center text-slate-500 w-12 text-center" data-testid="org-zoom-value">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => Math.min(1.6, z + 0.1))} data-testid="org-zoom-in" className="p-1.5 hover:bg-slate-50"><ZoomIn className="w-4 h-4"/></button>
          <button onClick={() => { setZoom(0.85); center(); }} data-testid="org-center" title="Centrer" className="p-1.5 hover:bg-slate-50 border-l border-slate-200"><Crosshair className="w-4 h-4"/></button>
        </div>
        {data?.can_edit && (
          <button onClick={() => setEdit(v => !v)} data-testid="org-edit-toggle" className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${edit ? "bg-emerald-600 text-white" : "bg-[#1d4ed8] text-white hover:bg-blue-800"}`}>
            {edit ? <><Check className="w-3.5 h-3.5"/>Terminer</> : <><Pencil className="w-3.5 h-3.5"/>Modifier l'organigramme</>}
          </button>
        )}
        <button onClick={onClose} data-testid="orgchart-close" className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5"/></button>
      </div>

      {edit && (
        <div className="bg-[#eef2ff] border-b border-[#c7d7fe] px-4 sm:px-6 py-2 flex items-center gap-2 flex-wrap text-xs" data-testid="org-edit-bar">
          <span className="font-semibold text-[#0b1b3a]">Mode édition :</span><span className="text-slate-600">glissez un nœud sur son nouveau responsable, ou</span>
          <select value={link.child} onChange={e => setLink(l => ({ ...l, child: e.target.value }))} className={inp} data-testid="org-link-child"><option value="">Collaborateur…</option>{people.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          <ChevronRight className="w-4 h-4 text-slate-400"/>
          <select value={link.parent} onChange={e => setLink(l => ({ ...l, parent: e.target.value }))} className={inp} data-testid="org-link-parent"><option value="">Rattaché à…</option>{Object.values(nodes).sort((a, b) => a.name.localeCompare(b.name, "fr")).map(p => <option key={p.id} value={p.id}>{p.name}{p.kind === "corner" ? " (corner)" : ""}</option>)}</select>
          <button onClick={() => { setParent(link.child, link.parent); setLink({ child: "", parent: "" }); }} disabled={!link.child || !link.parent} data-testid="org-link-add" className="px-3 py-1.5 rounded-lg bg-[#1d4ed8] text-white font-semibold flex items-center gap-1 disabled:opacity-50"><Plus className="w-3.5 h-3.5"/>Ajouter le lien</button>
        </div>
      )}

      <div id="orgchart-scroll" className="flex-1 overflow-auto p-8" data-testid="orgchart-canvas">
        {!data ? <div className="text-center text-sm text-slate-400 py-20">Chargement…</div> : (
          <div style={{ zoom }} className="w-max min-w-full flex flex-col items-center">
            {data.root ? <OrgNode id={data.root} depth={0} ctx={ctx}/> : <div className="text-sm text-slate-400">Aucun dirigeant identifié.</div>}
            {orphans.length > 0 && (
              <div className="mt-10 border-t border-dashed border-slate-300 pt-4" data-testid="org-orphans">
                <div className="text-xs font-bold text-slate-500 mb-3 text-center">Non rattachés ({orphans.length})</div>
                <div className="flex flex-wrap gap-3 justify-center max-w-4xl">{orphans.map(id => <OrgNode key={id} id={id} depth={9} ctx={ctx}/>)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const SELF = {};
function OrgNode({ id, depth, ctx }) {
  const { nodes, children, visible, edit, root, collapsed, setCollapsed, dragId, setDragId, setParent, onOpenPerson } = ctx;
    const n = nodes[id]; if (!n) return null;
    if (visible && !visible.has(id)) return null;
    const kids = (children[id] || []).filter(k => !visible || visible.has(k));
    const isCol = collapsed[id] ?? (n.kind === "corner" || (depth >= 2 && kids.length > 8) || depth >= 3);
    return (
      <div className="flex flex-col items-center" data-testid={`org-node-${id}`}>
        <div draggable={edit && id !== root} onDragStart={() => setDragId(id)} onDragOver={e => edit && e.preventDefault()}
             onDrop={e => { e.preventDefault(); if (edit && dragId && dragId !== id) setParent(dragId, id); setDragId(null); }}
             onClick={() => n.kind !== "corner" && onOpenPerson?.(n)}
             className={`relative bg-white border rounded-lg px-3 py-2 w-44 text-left cursor-pointer hover:shadow-md transition-shadow ${n.kind === "corner" ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200"} ${edit ? "ring-1 ring-dashed ring-[#93c5fd]" : ""}`}>
          <div className="flex items-center gap-2">
            {n.kind === "corner" ? <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0"><Store className="w-4 h-4"/></div> : <Avatar user={avatarOf(n)} size={32}/>}
            <div className="min-w-0">
              <div className="text-xs font-bold text-[#0b1b3a] truncate">{n.name}</div>
              <div className="text-[10px] text-slate-500 truncate">{n.poste}</div>
            </div>
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-1">
            <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold truncate ${SERVICE_STYLE[n.service] || "bg-slate-100 text-slate-600"}`}>{n.corner || n.service}</span>
            {edit && id !== root && <button onClick={e => { e.stopPropagation(); setParent(id, null); }} title="Supprimer le lien" data-testid={`org-unlink-${id}`} className="p-0.5 rounded text-rose-500 hover:bg-rose-50"><Unlink className="w-3 h-3"/></button>}
          </div>
          {kids.length > 0 && (
            <button onClick={e => { e.stopPropagation(); setCollapsed(c => ({ ...c, [id]: !isCol })); }} data-testid={`org-toggle-${id}`}
                    className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#1d4ed8] text-white grid place-items-center shadow text-[9px] font-bold">
              {isCol ? kids.length : <ChevronDown className="w-3 h-3"/>}
            </button>
          )}
        </div>
        {kids.length > 0 && !isCol && (
          <>
            <div className="w-px h-5 bg-slate-300 mt-2"/>
            <div className="flex items-start gap-3 relative pt-3">
              {kids.length > 1 && <div className="absolute top-0 h-px bg-slate-300" style={{ left: "calc(88px)", right: "calc(88px)" }}/>}
              {kids.map(k => (
                <div key={k} className="flex flex-col items-center">
                  <div className="w-px h-3 bg-slate-300 -mt-3"/>
                  {createElement(SELF.ref, { id: k, depth: depth + 1, ctx })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
SELF.ref = OrgNode;
